import React, { useState } from 'react';
import { Phone, Lock, User, MapPin, Calendar, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to extract error message from API response
const getErrorMessage = (data, defaultMsg) => {
  if (!data || !data.detail) return defaultMsg;
  
  if (typeof data.detail === 'string') {
    return data.detail;
  } else if (Array.isArray(data.detail)) {
    // Pydantic validation errors come as array of objects
    return data.detail.map(e => e.msg || e.message || 'Erreur de validation').join(', ');
  } else if (typeof data.detail === 'object') {
    return data.detail.msg || data.detail.message || defaultMsg;
  }
  return defaultMsg;
};

// Liste des principales villes de la RDC
const DRC_CITIES = [
  'KINSHASA',
  'LUBUMBASHI',
  'MBUJI-MAYI',
  'KANANGA',
  'KISANGANI',
  'BUKAVU',
  'GOMA',
  'MATADI',
  'BOMA',
  'KOLWEZI',
  'LIKASI',
  'TSHIKAPA',
  'KIKWIT',
  'MBANDAKA',
  'UVIRA',
  'BUTEMBO',
  'BENI',
  'KASONGO',
  'BANDUNDU',
  'KINDU',
  'KALEMIE',
  'GEMENA',
  'ISIRO',
  'BUNIA',
  'MUANDA',
  'MOANDA',
  'INONGO',
  'ZONGO',
  'AUTRE'
];

function PhoneAuth({ onAuthSuccess, onBack }) {
  const [step, setStep] = useState('phone'); // 'phone', 'otp', 'register'
  const [phoneNumber, setPhoneNumber] = useState('+243');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtherCity, setShowOtherCity] = useState(false);
  
  // Registration form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    date_of_birth: '',
    address: '',
    city: '',
    otherCity: ''
  });

  const handleCityChange = (e) => {
    const value = e.target.value;
    if (value === 'AUTRE') {
      setShowOtherCity(true);
      setFormData({...formData, city: value, otherCity: ''});
    } else {
      setShowOtherCity(false);
      setFormData({...formData, city: value, otherCity: ''});
    }
  };

  const normalizePhone = (raw) => {
    let p = (raw || '').replace(/[\s\-\.()]/g, '');
    if (p.startsWith('00243')) p = '+243' + p.slice(5);
    else if (p.startsWith('243')) p = '+' + p;
    else if (p.startsWith('0') && p.length === 10) p = '+243' + p.slice(1);
    if (p.startsWith('+2430') && p.length === 14) p = '+243' + p.slice(5);
    return p;
  };

  const sendOTP = async () => {
    const normalized = normalizePhone(phoneNumber);
    if (!/^\+243[0-9]{9}$/.test(normalized)) {
      setError('Numéro invalide. Exemple : +243 858 445 231 ou 0858445231');
      return;
    }
    setPhoneNumber(normalized);
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: normalized })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setStep('otp');
        setError('');
      } else {
        setError(getErrorMessage(data, 'Erreur lors de l\'envoi du code'));
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (otp.length < 4) {
      setError('Le code doit contenir au moins 4 chiffres');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phoneNumber, code: otp })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.is_new_user) {
          // Save registration token issued by server so /register can verify OTP proof
          if (data.registration_token) {
            sessionStorage.setItem('registration_token', data.registration_token);
          }
          setStep('register');
        } else {
          // Existing user - save token and redirect
          localStorage.setItem('auth_token', data.access_token);
          localStorage.setItem('user', JSON.stringify(data.user));
          onAuthSuccess(data.user);
        }
      } else {
        setError(getErrorMessage(data, 'Code invalide'));
      }
    } catch (err) {
      setError('Erreur de vérification');
    } finally {
      setLoading(false);
    }
  };

  const completeRegistration = async () => {
    if (!formData.first_name || !formData.last_name) {
      setError('Nom et prénom requis');
      return;
    }
    
    if (!formData.city) {
      setError('Veuillez sélectionner une ville');
      return;
    }
    
    if (formData.city === 'AUTRE' && !formData.otherCity.trim()) {
      setError('Veuillez préciser votre ville');
      return;
    }
    
    setLoading(true);
    setError('');
    
    // Determine final city value
    const finalCity = formData.city === 'AUTRE' 
      ? formData.otherCity.trim().toUpperCase() 
      : formData.city;
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phoneNumber,
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email || null,  // Send null instead of empty string
          date_of_birth: formData.date_of_birth || null,
          address: formData.address || null,
          city: finalCity,
          registration_token: sessionStorage.getItem('registration_token') || undefined
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        sessionStorage.removeItem('registration_token');
        localStorage.setItem('auth_token', data.access_token);
        localStorage.setItem('user', JSON.stringify(data.user));
        onAuthSuccess(data.user);
      } else {
        setError(getErrorMessage(data, 'Erreur d\'enregistrement'));
      }
    } catch (err) {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="phone-auth-container">
      <div className="phone-auth-card">
        {onBack && (
          <button className="btn-back-auth" onClick={onBack}>
            <ArrowLeft size={20} /> Retour
          </button>
        )}
        <div className="auth-header">
          <Phone size={48} className="auth-icon" />
          <h2>Connexion / Inscription</h2>
          <p>Centre Médical La Trinité</p>
        </div>

        {error && (
          <div className="error-message" data-testid="auth-error">
            {error}
          </div>
        )}

        {/* Step 1: Phone Number */}
        {step === 'phone' && (
          <div className="auth-step" data-testid="phone-step">
            <label>Numéro de Téléphone</label>
            <div className="phone-input-group">
              <Phone size={20} />
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="+243 XXX XXX XXX"
                data-testid="phone-input"
              />
            </div>
            <button
              onClick={sendOTP}
              disabled={loading}
              className="btn-primary"
              data-testid="send-otp-btn"
            >
              {loading ? 'Envoi...' : 'Recevoir le Code'}
            </button>
            <p className="auth-hint">
              Vous recevrez un code de vérification par WhatsApp
            </p>
          </div>
        )}

        {/* Step 2: OTP Verification */}
        {step === 'otp' && (
          <div className="auth-step" data-testid="otp-step">
            <label>Code de Vérification</label>
            <p className="step-info">Code envoyé au {phoneNumber}</p>
            <div className="otp-input-group">
              <Lock size={20} />
              <input
                type="text"
                inputMode="numeric"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="0000"
                maxLength="6"
                data-testid="otp-input"
              />
            </div>
            <button
              onClick={verifyOTP}
              disabled={loading}
              className="btn-primary"
              data-testid="verify-otp-btn"
            >
              {loading ? 'Vérification...' : 'Vérifier le Code'}
            </button>
            <button
              onClick={() => setStep('phone')}
              className="btn-link"
              data-testid="change-number-btn"
            >
              Changer de numéro
            </button>
          </div>
        )}

        {/* Step 3: Registration */}
        {step === 'register' && (
          <div className="auth-step register-step" data-testid="register-step">
            <div className="register-heading">
              <h3>Créer votre compte</h3>
              <p className="step-info">Complétez vos informations pour finaliser votre inscription</p>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-firstname">Prénom <span className="req">*</span></label>
                <input
                  id="reg-firstname"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  placeholder="Ex: Jean"
                  autoComplete="given-name"
                  data-testid="register-firstname"
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-lastname">Nom <span className="req">*</span></label>
                <input
                  id="reg-lastname"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  placeholder="Ex: Kabila"
                  autoComplete="family-name"
                  data-testid="register-lastname"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="reg-email">Email <span className="opt">(optionnel)</span></label>
              <input
                id="reg-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="votre.email@exemple.com"
                autoComplete="email"
                data-testid="register-email"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="reg-dob">Date de naissance <span className="opt">(optionnel)</span></label>
                <input
                  id="reg-dob"
                  type="date"
                  value={formData.date_of_birth}
                  max={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                  data-testid="register-dob"
                />
              </div>
              <div className="form-group">
                <label htmlFor="reg-city">Ville <span className="req">*</span></label>
                <select
                  id="reg-city"
                  value={formData.city}
                  onChange={handleCityChange}
                  data-testid="register-city"
                >
                  <option value="">— Sélectionnez une ville —</option>
                  {DRC_CITIES.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>
            </div>

            {showOtherCity && (
              <div className="form-group">
                <label htmlFor="reg-other-city">Précisez votre ville</label>
                <input
                  id="reg-other-city"
                  type="text"
                  placeholder="Entrez le nom de votre ville"
                  value={formData.otherCity}
                  onChange={(e) => setFormData({...formData, otherCity: e.target.value})}
                  data-testid="register-other-city"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="reg-address">Adresse <span className="opt">(optionnel)</span></label>
              <input
                id="reg-address"
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                placeholder="Ex: Avenue Mobutu, Quartier..."
                autoComplete="street-address"
                data-testid="register-address"
              />
            </div>

            <button
              onClick={completeRegistration}
              disabled={loading || !formData.first_name.trim() || !formData.last_name.trim()}
              className="btn-primary btn-register-submit"
              data-testid="complete-registration-btn"
            >
              {loading ? 'Création…' : 'Créer mon compte'}
            </button>
            <p className="auth-hint register-hint">
              En créant votre compte, vous acceptez les <a href="/mentions-legales" target="_blank" rel="noopener noreferrer">mentions légales</a> du CMT.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PhoneAuth;
